import React, { useState } from 'react';
import { getTopBooks, bookCategories } from '../services/bookService';
import { useData } from '../context/DataContext';
import ItemList from './shared/ItemList';
import FilterSection from './shared/FilterSection';
import './BookList.css';

function BookList() {
  const { preloadedBooks } = useData();
  
  // Initialize filters from localStorage or use defaults
  const [activeFilters, setActiveFilters] = useState(() => {
    const savedFilters = localStorage.getItem('bookActiveFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      year: '',
      subject: ''
    };
  });

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? '' : value
    }));
  };

  // Define filter groups for books
  const bookFilterGroups = [
    {
      id: 'year',
      title: 'Time Period',
      options: bookCategories.years.map(year => ({
        label: year.label,
        value: year.value,
        className: year.label === 'All Time' || year.label === 'Older Books' ? 'all-time' : 
                  year.label.includes('s') ? 'decade' : 'year'
      }))
    },
    {
      id: 'subject',
      title: 'Subject',
      options: bookCategories.subjects.map(subject => ({
        label: subject.label,
        value: subject.value,
        className: subject.label === 'All Genres' ? 'all-time' : 'year'
      }))
    }
  ];

  // Render the filter section
  const renderFilterSection = () => {
    return (
      <FilterSection 
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        filterGroups={bookFilterGroups}
      />
    );
  };

  // Format book for display
  const formatBookForDisplay = (book) => {
    return {
      id: book.id.replace(/^\//, ''),
      title: book.title,
      year: book.year,
      authors: book.author ? [{ name: book.author }] : [],
      imageComponent: (
        <div className="image-container">
          <img 
            src={book.coverId ? `https://covers.openlibrary.org/b/id/${book.coverId}-M.jpg` : 'https://via.placeholder.com/200x300?text=No+Cover'} 
            alt={book.title} 
            className="book-cover" 
          />
        </div>
      ),
      contentComponent: (
        <div className="card-content">
          <div className="title-container">
            <h2>{book.title}</h2>
          </div>
          <div className="middle-content">
            <div className="book-genre">
              {book.subjects === undefined ? (
                <span className="genre-loading-indicator">
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                  <span className="loading-dot"></span>
                </span>
              ) : book.subjects && book.subjects.length > 0 ? (
                book.subjects.map((subject, index) => (
                  <span key={index} className="genre-item">
                    {subject}
                    {index < Math.min(book.subjects.length, 2) - 1 ? ', ' : ''}
                  </span>
                ))
              ) : (
                <span className="no-genres">No genres available</span>
              )}
            </div>
            <div className="book-info">
              <span>{book.author}</span>
              <span>{book.year}</span>
            </div>
          </div>
          <div className="book-card-footer">
            {book.rating === 'loading' ? (
              <div className="book-rating loading-rating">
                <span className="loading-dot"></span>
                <span className="loading-dot"></span>
                <span className="loading-dot"></span>
              </div>
            ) : book.rating !== 'No rating' ? (
              <div className="book-rating">
                {book.rating}/5
              </div>
            ) : null}
          </div>
        </div>
      )
    };
  };

  return (
    <ItemList
      type="book"
      fetchItems={getTopBooks}
      preloadedItems={preloadedBooks}
      activeFilters={activeFilters}
      renderFilterSection={renderFilterSection}
      title="Top Rated Books"
      formatItemForDisplay={formatBookForDisplay}
    />
  );
}

export default BookList; 